<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { ResourceEditorFormSubmitEventPayload } from '@Quest/Resources/Components/ResourceEditor'
    import type { ResourceEditorViewModel } from '@Quest/Resources/Components/ResourceEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ResourceEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'form-submit', payload: ResourceEditorFormSubmitEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const confirmButtonLabel = computed(() => vm.value.confirmButtonLabel)
    const modalTitle = computed(() => vm.value.modalTitle)
    const costPerUnitVisible = computed(() => vm.value.costPerUnitVisible)

    // writable computeds (replace getter + setter pairs)

    const name = computed<string>({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    const unit = computed<string>({
        get: () => vm.value.unit,
        set: v => { vm.value.unit = v }
    })

    const classification = computed<string>({
        get: () => vm.value.classification,
        set: v => { vm.value.classification = v }
    })

    const costPerUnit = computed<number | null>({
        get: () => vm.value.costPerUnit,
        set: v => { vm.value.costPerUnit = v }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        emit(
            'form-submit',
            new ResourceEditorFormSubmitEventPayload(
                vm.value.id,
                name.value,
                unit.value,
                classification.value,
                costPerUnit.value
            )
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="resource-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required minlength="2" maxlength="100" />
                            </div>
                            <div class="form-group">
                                <label>Unit:</label>
                                <input type="text" v-model.trim="unit" class="form-control" maxlength="50" />
                            </div>
                            <div class="form-group">
                                <label>P&L cost classification:</label>
                                <select class="form-control" v-model="classification">
                                    <option value="OpEx">OpEx</option>
                                    <option value="COGS">COGS</option>
                                    <option value="Other">Other expenses</option>
                                    <option value="None">None</option>
                                </select>
                            </div>
                            <div class="form-group" v-if="costPerUnitVisible">
                                <label>Cost per unit:</label>
                                <input type="number" v-model.number="costPerUnit" class="form-control" min="0" step="any" />
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="resource-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>